import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AuthPage } from "../components/AuthPage";
import { Banner } from "../components/ui/Banner";
import { Button } from "../components/ui/Button";
import { useSuccessToast } from "../components/ui/Toast";
import { requestPasswordReset } from "../lib/account-api";
import { errorMessage } from "../lib/error-message";

import styles from "./forgot-password.module.css";

/**
 * A forgotten password. The answer is the same whether or not the address
 * has an account, so the page says the same thing either way: it cannot
 * know, and saying otherwise would tell anybody who typed an address.
 * Said as a toast on the way back to login, which is where the link in
 * the email ends up anyway.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useSuccessToast();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const address = email.trim();
      await requestPasswordReset(address);
      toast(
        `If ${address} has an account, a link to reset its password is on its way. It works for one hour.`,
      );
      navigate("/login", { state: { email: address } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPage
      title="Reset password"
      description="Enter the email you log in with and we will send you a link."
    >
      <form onSubmit={onSubmit} className={styles.stack}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <input
            autoComplete="email"
            className={styles.input}
            disabled={submitting}
            inputMode="email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
        </label>

        {error ? <Banner variant="error">{error}</Banner> : null}

        <div className={styles.actions}>
          <Button
            className={styles.submit}
            disabled={submitting}
            loading={submitting}
            type="submit"
          >
            Send reset link
          </Button>
        </div>
        <p className={styles.footNote}>
          <Link className={styles.footLink} to="/login">
            Back to login
          </Link>
        </p>
      </form>
    </AuthPage>
  );
}
